import * as React from "react";
import {
  DefaultButton,
  Pivot,
  PivotItem,
  Stack,
  Separator,
  Spinner,
  Text,
} from "@fluentui/react";
import useAxios from "axios-hooks";
import { useCallback, useEffect, useState,  useRef } from "react";
import {useBoolean} from "@fluentui/react-hooks";
import { Prompt, useHistory, useParams } from "react-router-dom";
import { Formik } from "formik";

import PrimaryButton from "../../Forms/PrimaryButton";
import TextField from '../../Forms/TextField'
import Form from "../../Forms/Form";
import CobrandDropDown from "../Checklist/Checklists/Forms/CobrandDropDown";
import CreateEditCreditReportActiveUserThresholdingModal from "./CreateEditCreditReportActiveUserThresholdModal";
import FeatureSummary from "../../Util/FeatureSummary";


export const CreateEditCreditReportActiveUserThresholding = () => {
    const history = useHistory();

    let { maxActiveCreditUsers: maxActiveCreditUsersParam } = useParams();

    const [maxActiveCreditUsers, setMaxActiveCreditUsers] = useState(maxActiveCreditUsersParam);
    const [maxActiveCreditUsersValid, setMaxActiveCreditUsersValid] = useState(false);

    const [hideCobrandsTagClients, sethideCobrandsTagClients] = useState(!maxActiveCreditUsersParam);

    const [reloadEdit, setReloadEdit] = useState(false);

    const createMode = !maxActiveCreditUsersParam;

    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    const [showConfirmModal, { toggle: toggleConfirmModal }] = useBoolean(false);

    const formRef = useRef()

    const [{ data }, submitForm] = useAxios(
    { url: `credit-reports/thresholding`, method: "post" },
    { manual: true }
    );
    
    const [
        { data: threshold },
        getThresholdData,
    ] = useAxios(
        { url: `credit-reports/thresholding/${maxActiveCreditUsers}`, method: "get" },
        {manual: "true"}
    );

    useEffect(() => {
        if (!createMode && maxActiveCreditUsers && !threshold) {
            getThresholdData();
        }

        if (!createMode && threshold) {
            setMaxActiveCreditUsersValid(true);
        }
    }, [maxActiveCreditUsers, threshold])

    useEffect(() => {
        if (reloadEdit) {
            history.push(`/credit-report-feature/thresholding/${maxActiveCreditUsers}/edit`);
        }
    }, [reloadEdit])

    if (!createMode && maxActiveCreditUsers && !threshold) {
        return <Spinner className="absolute-center" text="Loading Threshold..." />;
    }

  return (
    <Formik
        innerRef={formRef}
        initialValues={mapInitialValues(threshold)}
        onSubmit={async (values) => {

            try {
                await submitForm({
                    data: mapValuesForSubmit(values),
                });
                setReloadEdit(true);
            } 
            catch (e) {
            throw e;
            }
        }}
        isInitialValid={threshold}
        validate={() => {

      }}
    >
      {({ dirty, width }) => {

        return (
          <>
            <Prompt
              when={(dirty || hasUnsavedChanges) && !reloadEdit}
              message="You have unsaved changes, are you sure you want to leave?"
            />
            <Form>
                <TextField 
                    label="Max Active Credit Users Threshold"
                    name="maxActiveCreditUsers"
                    disabled={!createMode || !hideCobrandsTagClients}
                    description="This field thresholds the maximum number of active users. 'Active' means we are being billed for the users via Equifax."
                    value={maxActiveCreditUsers}
                    validate={(val) => { 
                        let error; 
                        setMaxActiveCreditUsers(val);

                        if (!val) {
                            error ="required.";
                        } 
                        
                        if (!/^[0-9]+$/.test(val)) {
                            error = "Must be an integer."
                        }

                        if (parseInt(val) < 1) {
                            error = "Must be greater or equal to 1."
                        }

                        setMaxActiveCreditUsersValid(!error);

                        return error;
                    }}
                    required
                ></TextField>

                {createMode && hideCobrandsTagClients &&
                    <PrimaryButton
                            text={"Select Threshold"}
                            type="button"
                            disabled={!maxActiveCreditUsersValid || !maxActiveCreditUsers}
                            onClick={() => {
                                getThresholdData().then((res) => {

                                    if (res.data) {
                                        setReloadEdit(true);
                                    }
                                    else
                                    {
                                        sethideCobrandsTagClients(false);
                                    }

                                })

                            }}
                            />
                }
                {!hideCobrandsTagClients &&
                    <>
                        <PrimaryButton
                            type="button"
                            text={"Change Threshold (Clears Cobrands, Cobrand Tags, Clients if set)"}
                            onClick={() => {
                                setMaxActiveCreditUsers(null);
                                setMaxActiveCreditUsersValid(false);
                                sethideCobrandsTagClients(true);

                                if (!createMode) {
                                    history.push(`/credit-report-feature/thresholding/create`);
                                }
                            }}
                        />
                        <CobrandDropDown                 
                            name={"cobrandIds"}
                            label="Cobrands"
                            byNameOrId="id"
                        />
                    </>
                }
                <>
                    <Separator />
                    <Stack
                        horizontal
                        styles={{
                        root: {
                            height: 50,
                            ...(width && { width }),
                        },
                        }}
                        horizontalAlign={ "space-between"}
                        verticalAlign="center"
                    >
                        <DefaultButton onClick={() => {history.push("/credit-report-feature/thresholding")}} >
                            {"Back to List"}
                        </DefaultButton>

                        {!hideCobrandsTagClients &&
                            <PrimaryButton
                                text={"Save Threshold"}
                                type={"button"}
                                onClick={toggleConfirmModal }
                            />
                        }
                    </Stack>
                </>
                <CreateEditCreditReportActiveUserThresholdingModal
                        showModal={showConfirmModal}
                        toggleShowModal={toggleConfirmModal} 
                        submitForm={() => {formRef.current.handleSubmit()}}
                ></CreateEditCreditReportActiveUserThresholdingModal>
                <FeatureSummary
                    label="About Credit Report Thresholding"
                    summary="Configure active user thresholds for credit report access. Set a maximum number of active credit users and assign cobrands to that threshold tier. This helps manage credit report licensing costs by limiting concurrent active users per cobrand group."
                />
            </Form>
          </>
        );
      }}
    </Formik>
  );
};

export function mapInitialValues(threshold) {
  if (threshold)
    return {
      maxActiveCreditUsers: threshold.maxActiveCreditUsers,
      cobrandIds: threshold.cobrandIds ? threshold.cobrandIds : [],
      cobrands: threshold.cobrands
    };
  else return {
    maxActiveCreditUsers: null,
    cobrandIds: [],
    cobrands: ""
  };
}

function mapValuesForSubmit(values) {
  return  {
    maxActiveCreditUsers: values.maxActiveCreditUsers,
    cobrandIds: values.cobrandIds,
    cobrands: values.cobrandIds.join(", ")
  };
}


export default CreateEditCreditReportActiveUserThresholding;
